import { useCallback, useState } from 'react'
import axios from 'axios'
import { apiRoot } from '../config'
import type { SpellcheckResult, SpellcheckWord } from '../Types/SpellcheckTypes'

/**
 * Custom hook for spellchecking text
 */
export const useSpellcheck = () => {
  const [isChecking, setIsChecking] = useState<boolean>(false)

  /**
   * Check spelling for the provided text by making an API call
   *
   * @param text - The text to check
   * @param language - The language code to use for spellchecking
   * @returns A promise with the spellcheck results
   */
  const checkSpelling = useCallback(
    async (text: string, language: string): Promise<SpellcheckResult | null> => {
      if (!text || text.trim() === '') {
        return null
      }

      try {
        setIsChecking(true)
        const response = await axios.post(apiRoot + '/api/spellcheck/', {
          text,
          language,
        })
        return response.data
      } catch (error) {
        console.error('Spellcheck error:', error)
        return null
      } finally {
        setIsChecking(false)
      }
    },
    []
  )

  /**
   * Replace a misspelled word with a suggestion in the text
   *
   * @param text - Original text
   * @param word - Misspelled word with position info
   * @param suggestion - The suggestion to replace with
   * @returns Updated text with the suggestion
   */
  const replaceWord = useCallback(
    (text: string, word: SpellcheckWord, suggestion: string): string => {
      // Simple text replacement based on word position
      return text.substring(0, word.start) + suggestion + text.substring(word.end)
    },
    []
  )

  return {
    checkSpelling,
    replaceWord,
    isChecking,
  }
}
